import {useCallback, useRef, useState} from 'react';
import {Link} from 'react-router-dom';

import Button from 'components/button/Button';
import Container from 'components/container/Container';
import Input from 'components/input/Input';
import {getServices} from 'services/index';
import {IAuthUser, UserValidationErrors} from 'services/User';
import Constant from 'utils/Constant';

const RegisterPage = () => {
  const input = useRef<IAuthUser>({});
  const [errors, setErrors] = useState({} as UserValidationErrors);
  const [validating, setValidating] = useState(false);

  const performSignUp = useCallback(async () => {
    const {authService} = getServices();
    setErrors({} as UserValidationErrors);
    const validation = authService.validateRegistration(input.current);
    const items = Object.keys(validation) as (keyof IAuthUser)[];
    input.current.termsAcceptedOn = new Date().toISOString();
    if (items.length > 0) {
      setErrors(validation);
      return;
    }
    setValidating(true);
    const emailExists = await authService.checkUserExists(input.current.email!);
    if (emailExists) {
      setErrors((e) => ({
        ...e,
        email: {
          status: 'error',
          description: 'This email has been used already.',
        },
      }));
      setValidating(false);
      return;
    }
    const user: IAuthUser = {
      ...input.current,
    };
    // Do not save password to the firestore.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {password, confirmPassword, ...userProfile} = user;
    await authService.register(user);
    await authService.createProfile({
      subscription: {
        title: 'Free',
        planCmsId: Constant.freePlanCmsId,
      },
      summary: {
        wishlist: {
          id: 'wishlist',
          name: 'wishlist',
          count: 0,
        },
        plans: {},
      },
      ...userProfile,
    });
    setValidating(false);
  }, []);

  return (
    <Container>
      <form
        className="w-full max-w-md"
        onSubmit={(event) => {
          event.preventDefault();
          performSignUp();
        }}
      >
        <label className="font-medium text-brand-forest mb-2 inline-block">
          Step 1 of 4
        </label>
        <h1 className="text-3xl font-bold mb-4 text-brand-forest">
          Sign up and create your password
        </h1>
        <div className="grid md:grid-cols-2 gap-2">
          <Input
            message={errors.firstName}
            placeholder="First name"
            onInput={(event) => {
              input.current.firstName = event.currentTarget.value;
            }}
            required
          />
          <Input
            message={errors.lastName}
            placeholder="Last name"
            onInput={(event) => {
              input.current.lastName = event.currentTarget.value;
            }}
            required
          />
        </div>
        <Input
          message={errors.email}
          placeholder="Email"
          type="email"
          onInput={(event) => {
            input.current.email = event.currentTarget.value;
          }}
          required
        />
        <Input
          message={errors.password}
          placeholder="Password"
          type="password"
          onInput={(event) => {
            input.current.password = event.currentTarget.value;
          }}
          required
        />
        <Input
          message={errors.confirmPassword}
          placeholder="Confirm password"
          type="password"
          onInput={(event) => {
            input.current.confirmPassword = event.currentTarget.value;
          }}
          required
        />
        <Button title="Next" onClick={performSignUp} disabled={validating} />
        <Link to="/">
          <p className="text-center text-label uppercase tracking-widest font-medium">
            already have an account{' '}
            <span className="text-brand-forest">log in</span>
          </p>
        </Link>
        <div className="my-4 text-text text-xs mt-2">
          By clicking Next, you agree to our{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.halth.com/terms-and-conditions"
            className="underline font-bold"
          >
            Terms
          </a>{' '}
          and acknowledge that you have read our{' '}
          <a
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.halth.com/privacy-policy"
            className="underline font-bold"
          >
            Privacy Policy
          </a>
          , which explains how HaltH collects and handles your personal
          information in accordance with HaltH's Privacy Policy.
        </div>
      </form>
    </Container>
  );
};

export default RegisterPage;
